import {
  DateField,
  TextField,
  CardSubtitleText,
  EmptyState,
  DialogIcon,
  CardSurface,
  CompactActionButton,
  Dialog,
  DialogActions,
  DialogHeader,
  IconButton,
  MoonFilterChip,
  MoonFormField,
  MoonNumericField,
  PrimaryButton,
  SecondaryButton,
  SectionTitle,
  TextActionButton,
  ActionBanner,
  type InlineOption,
} from "@louise/moon-ui";
import { parseDateText } from "./paymentRecordMapping";
import { useEffect, useState, type ReactNode } from "react";
import { Image, Pressable, ScrollView, Text, View } from "react-native";
import {
  Building2,
  CheckCircle2,
  ChevronRight,
  CircleDollarSign,
  Clock3,
  Dna,
  FileText,
  Landmark,
  MessageCircle,
  Pencil,
  Pill,
  Plus,
  ShieldCheck,
  Snowflake,
  Stethoscope,
  Syringe,
  TestTube,
  Trash2,
  UploadCloud,
  X,
} from "lucide-react-native";
import type { LucideIcon } from "lucide-react-native";
import { useDocument } from "@src/packages/documents/hooks/useDocuments";
import DocumentPreviewModal, {
  type PreviewDocumentInfo,
} from "../DocumentPreviewModal";
import { colors } from "@louise/moon-ui/theme/tokens";
import {
  formatCurrency,
  formatDate,
  getCurrencyAffixes,
  type Locale,
  useTranslations,
} from "@src/packages/i18n";
import {
  usePressCapture,
  pressLabel,
} from "@src/packages/analytics/usePressCapture";
import { EMBER } from "../../../onboarding/theme";
import type {
  CostPaymentAllocationKind,
  CostPaymentDocumentParam,
  CostPaymentLineItemParam,
  CostPaymentRecordParam,
  CostPaymentRecordStatus,
  CostPaymentSplitLineParam,
  CostPaymentTaxEligibility,
} from "../../types";
import { TAX_EXPENSE_RECEIPT_ILLUSTRATION } from "./taxReviewAssets";
import { semanticColors } from "@louise/theme/tokens";

const DELETE_CONFIRMATION_ILLUSTRATION =
  require("../../../assets/images/you-icons/trash-bin.png") as number;

const PAYMENT_SPLIT_COLORS = {
  paid: "#F4B34F",
  claims: "#5B332C",
  left: "#B76545",
} as const;

type StatusBadgeTone =
  "neutral" | "gold" | "coral" | "green" | "orange" | "blue" | "purple";

type PaymentAction = {
  label: string;
  icon: LucideIcon;
  onPress: () => void;
  /** The option she has chosen, for actions that act as a choice. */
  selected?: boolean;
};

export type TaxReviewDecision = "include" | "exclude" | "unknown";

const UNKNOWN_TAX_ELIGIBILITY: CostPaymentTaxEligibility = {
  expenseType: "unknown",
  careRecipient: "unknown",
  insuranceOutcome: "unknown",
};

type Props = {
  record: CostPaymentRecordParam;
  locale: Locale;
  onClose: () => void;
  onAttachDocument?: () => void;
  onDeleteDocument?: (documentId: string) => void | Promise<void>;
  onReviewInsurance?: () => void;
  onOpenFinancing?: () => void;
  onOpenTaxTracker?: () => void;
  onAskChat?: (record: CostPaymentRecordParam) => void;
  onSaveRecord?: (record: CostPaymentRecordParam) => void | Promise<void>;
  onDeleteRecord?: () => void | Promise<void>;
  taxReviewDecision?: TaxReviewDecision;
  onTaxReviewDecision?: (
    decision: Exclude<TaxReviewDecision, "unknown">,
  ) => void | Promise<void>;
  onResetTaxReviewDecision?: () => void | Promise<void>;
  taxEligibility?: CostPaymentTaxEligibility | null;
  onTaxEligibilityChange?: (
    eligibility: CostPaymentTaxEligibility,
  ) => void | Promise<void>;
};

type AllocationDraft = {
  id: string;
  kind: CostPaymentAllocationKind;
  label: string;
  amount: string;
};

type LineItemDraft = {
  id: string;
  label: string;
  amount: string;
  detail: string;
  allocations: AllocationDraft[];
};

type RecordDraft = {
  label: string;
  provider: string;
  date: string;
  status: CostPaymentRecordStatus;
  detail: string;
  lineItems: LineItemDraft[];
};

const PAYMENT_STATUS_OPTIONS: {
  value: CostPaymentRecordStatus;
  label: string;
}[] = [
  { value: "paid", label: "Paid" },
  { value: "submitted", label: "Submitted" },
  { value: "waiting-eob", label: "Waiting EOB" },
  { value: "needs-receipt", label: "Needs receipt" },
  { value: "due", label: "Due" },
  { value: "review", label: "Review" },
];

const ALLOCATION_KIND_OPTIONS: {
  value: CostPaymentAllocationKind;
  label: string;
}[] = [
  { value: "patient", label: "You" },
  { value: "insurance", label: "Insurance" },
  { value: "employer", label: "Employer" },
  { value: "financing", label: "Financing" },
  { value: "clinic", label: "Clinic" },
  { value: "other", label: "Other" },
];

// Common charges, each with an icon that names the kind of care at a glance.
const LINE_ITEM_PRESETS: { label: string; icon: LucideIcon }[] = [
  { label: "Carrier screening", icon: Dna },
  { label: "Consult", icon: Stethoscope },
  { label: "Medication", icon: Pill },
  { label: "Anesthesia", icon: Syringe },
  { label: "Lab", icon: TestTube },
  { label: "Storage", icon: Snowflake },
];

function getStatusBadge(status: CostPaymentRecordStatus): {
  label: string;
  tone: StatusBadgeTone;
} {
  if (status === "paid") return { label: "Paid", tone: "green" };
  if (status === "submitted") return { label: "Submitted", tone: "blue" };
  if (status === "waiting-eob") {
    return { label: "Waiting for EOB", tone: "neutral" };
  }
  if (status === "needs-receipt") {
    return { label: "Receipt needed", tone: "gold" };
  }
  if (status === "partially-covered") {
    return { label: "Partially covered", tone: "gold" };
  }
  if (status === "ready-reimbursement") {
    return { label: "Ready for claim", tone: "green" };
  }
  if (status === "denied") return { label: "Denied", tone: "coral" };
  if (status === "review") return { label: "Needs review", tone: "gold" };
  return { label: "Due", tone: "coral" };
}

/**
 * One choice from a short list, as filter chips in a wrapping row, each as
 * wide as its label. Every choice in this dialog uses it. A chip reports the
 * press id the option picker it replaces did.
 */
function ChoiceChips({
  options,
  value,
  onChange,
}: {
  options: InlineOption[];
  value: string;
  onChange: (id: string) => void;
}) {
  return (
    <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
      {options.map((option) => (
        <MoonFilterChip
          key={option.id}
          label={option.label}
          icon={option.icon}
          selected={option.id === value}
          onPress={() => onChange(option.id)}
          action={option.action ?? option.id}
          testID={option.testID}
        />
      ))}
    </View>
  );
}

function getStatusIcon(status: CostPaymentRecordStatus): LucideIcon {
  if (status === "paid" || status === "ready-reimbursement") {
    return CheckCircle2;
  }
  if (status === "submitted" || status === "waiting-eob") {
    return Clock3;
  }
  return CircleDollarSign;
}

function getPrimaryAmountLabel(record: CostPaymentRecordParam) {
  if (record.status === "paid") return "";
  if (record.status === "submitted" || record.status === "waiting-eob") {
    return "Claim submitted";
  }
  if (record.status === "needs-receipt") return "Receipt total";
  if (record.status === "review") return "Needs review";
  if (record.status === "denied") return "Denied claim";
  if (record.status === "partially-covered") return "Out of pocket";
  if (record.status === "ready-reimbursement") return "Ready to submit";
  return "Left to pay";
}

export function buildPaymentRecordChatPrompt(
  record: CostPaymentRecordParam,
  locale: Locale,
) {
  const amount =
    record.amount > 0 ? formatCurrency(record.amount, locale) : "not listed";
  const billedAmount =
    record.billedAmount > 0
      ? formatCurrency(record.billedAmount, locale)
      : "not listed";
  const breakdown = record.splitLines
    .map((line) => `${line.label}: ${formatCurrency(line.amount, locale)}`)
    .join("; ");

  return [
    "I need help deciding what to do next about a fertility-care payment record.",
    `Record: ${record.label}`,
    `Status: ${getStatusBadge(record.status).label}`,
    `Provider: ${record.provider || "not listed"}`,
    `Date: ${record.date || "not listed"}`,
    `Record amount: ${amount}`,
    `Billed amount: ${billedAmount}`,
    `Coverage context: ${record.coverage || "not listed"}`,
    `Financing context: ${record.financing || "not listed"}`,
    `Current detail: ${record.detail || "not listed"}`,
    `Payment breakdown: ${breakdown || "not listed"}`,
    "Please explain the possible next steps, what documents or questions I should gather, and which path to try first. Ask clarifying questions before making assumptions about my insurance or coverage.",
  ].join("\n");
}

function buildActions({
  onAttachDocument,
  hasUploadedDocuments,
  onReviewInsurance,
  onOpenFinancing,
  onOpenTaxTracker,
  onAskChat,
  record,
}: {
  onAttachDocument?: () => void;
  hasUploadedDocuments: boolean;
  onReviewInsurance?: () => void;
  onOpenFinancing?: () => void;
  onOpenTaxTracker?: () => void;
  onAskChat?: (record: CostPaymentRecordParam) => void;
  record: CostPaymentRecordParam;
}): PaymentAction[] {
  const actions: PaymentAction[] = [];

  if (onReviewInsurance) {
    actions.push({
      label: "Review insurance",
      icon: ShieldCheck,
      onPress: onReviewInsurance,
    });
  }

  if (onOpenFinancing) {
    actions.push({
      label: "Financing options",
      icon: Landmark,
      onPress: onOpenFinancing,
    });
  }

  if (onOpenTaxTracker) {
    actions.push({
      label: "Tax tracker",
      icon: FileText,
      onPress: onOpenTaxTracker,
    });
  }

  if (onAskChat) {
    actions.push({
      label: "What can I do about this?",
      icon: MessageCircle,
      onPress: () => onAskChat(record),
    });
  }

  if (onAttachDocument && hasUploadedDocuments) {
    actions.push({
      label: "Add document",
      icon: UploadCloud,
      onPress: onAttachDocument,
    });
  }

  return actions;
}

function getOverviewAmount(record: CostPaymentRecordParam) {
  if (record.amount > 0) return record.amount;
  if (record.billedAmount > 0) return record.billedAmount;
  return null;
}

function getFallbackSegmentColor(record: CostPaymentRecordParam) {
  if (record.status === "paid" || record.status === "ready-reimbursement") {
    return PAYMENT_SPLIT_COLORS.paid;
  }

  if (record.status === "submitted" || record.status === "waiting-eob") {
    return PAYMENT_SPLIT_COLORS.claims;
  }

  if (record.status === "needs-receipt" || record.status === "review") {
    return PAYMENT_SPLIT_COLORS.paid;
  }

  return PAYMENT_SPLIT_COLORS.left;
}

function getSegmentDisplayColor(line: CostPaymentSplitLineParam) {
  const label = line.label.toLowerCase();

  if (
    label.includes("paid") ||
    label.includes("receipt") ||
    label.includes("reimbursement")
  ) {
    return PAYMENT_SPLIT_COLORS.paid;
  }

  if (
    label.includes("claim") ||
    label.includes("insurance") ||
    label.includes("coverage")
  ) {
    return PAYMENT_SPLIT_COLORS.claims;
  }

  if (
    label.includes("left") ||
    label.includes("responsibility") ||
    label.includes("out-of-pocket") ||
    label.includes("patient")
  ) {
    return PAYMENT_SPLIT_COLORS.left;
  }

  return line.color;
}

function parseMoneyInput(value: string) {
  const parsed = Number(value.replace(/[^0-9.]/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatMoneyInput(value: number) {
  return value > 0 ? String(value) : "";
}

function getAllocationLabel(kind: CostPaymentAllocationKind) {
  if (kind === "patient") return "Paid by you";
  if (kind === "insurance") return "Insurance";
  if (kind === "employer") return "Employer benefit";
  if (kind === "financing") return "Financing";
  if (kind === "clinic") return "Clinic adjustment";
  return "Other";
}

function getAllocationColor(kind: CostPaymentAllocationKind) {
  if (kind === "insurance") return PAYMENT_SPLIT_COLORS.claims;
  if (kind === "financing" || kind === "clinic") {
    return PAYMENT_SPLIT_COLORS.left;
  }
  return PAYMENT_SPLIT_COLORS.paid;
}

function inferAllocationKind(label: string): CostPaymentAllocationKind {
  const normalized = label.toLowerCase();

  if (normalized.includes("insurance") || normalized.includes("claim")) {
    return "insurance";
  }
  if (normalized.includes("employer") || normalized.includes("benefit")) {
    return "employer";
  }
  if (normalized.includes("financing")) return "financing";
  if (normalized.includes("clinic")) return "clinic";
  if (
    normalized.includes("left") ||
    normalized.includes("balance") ||
    normalized.includes("responsibility")
  ) {
    return "patient";
  }

  return "patient";
}

function createFallbackSplitLine(
  record: CostPaymentRecordParam,
): CostPaymentSplitLineParam {
  const amount = getOverviewAmount(record) ?? 0;

  return {
    id: `${record.id}-manual-line`,
    label: getPrimaryAmountLabel(record) || "Paid by you",
    amount,
    detail: record.source,
    color: getFallbackSegmentColor(record),
  };
}

function createFallbackLineItems(
  record: CostPaymentRecordParam,
): CostPaymentLineItemParam[] {
  const splitLines =
    record.splitLines.length > 0
      ? record.splitLines
      : [createFallbackSplitLine(record)];
  const total =
    record.billedAmount > 0
      ? record.billedAmount
      : splitLines.reduce((sum, line) => sum + line.amount, 0);

  return [
    {
      id: `${record.id}-line-item`,
      label: record.label,
      amount: total,
      detail: record.detail,
      allocations: splitLines.map((line) => ({
        id: line.id,
        kind: inferAllocationKind(line.label),
        label: line.label,
        amount: line.amount,
      })),
    },
  ];
}

function createDraftFromRecord(record: CostPaymentRecordParam): RecordDraft {
  const lineItems =
    record.lineItems && record.lineItems.length > 0
      ? record.lineItems
      : createFallbackLineItems(record);

  return {
    label: record.label,
    provider: record.provider,
    date: record.occurredOn ?? parseDateText(record.date) ?? "",
    status: record.status,
    detail: record.note ?? record.detail,
    lineItems: lineItems.map((lineItem) => ({
      id: lineItem.id,
      label: lineItem.label,
      amount: formatMoneyInput(lineItem.amount),
      detail: lineItem.detail,
      allocations: lineItem.allocations.map((allocation) => ({
        id: allocation.id,
        kind: allocation.kind,
        label: allocation.label,
        amount: formatMoneyInput(allocation.amount),
      })),
    })),
  };
}

function aggregateSplitLines(
  lineItems: CostPaymentLineItemParam[],
): CostPaymentSplitLineParam[] {
  const grouped = new Map<
    string,
    {
      id: string;
      label: string;
      amount: number;
      detailItems: string[];
      color: string;
    }
  >();

  lineItems.forEach((lineItem) => {
    lineItem.allocations.forEach((allocation) => {
      if (allocation.amount <= 0) return;
      const key = `${allocation.kind}:${allocation.label}`;
      const existing = grouped.get(key);

      if (existing) {
        existing.amount += allocation.amount;
        existing.detailItems.push(lineItem.label);
        return;
      }

      grouped.set(key, {
        id: `${allocation.kind}-${allocation.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`,
        label: allocation.label,
        amount: allocation.amount,
        detailItems: [lineItem.label],
        color: getAllocationColor(allocation.kind),
      });
    });
  });

  return Array.from(grouped.values()).map((line) => ({
    id: line.id,
    label: line.label,
    amount: line.amount,
    detail:
      line.detailItems.length === 1
        ? (line.detailItems[0] ?? "")
        : `${line.detailItems.length} items`,
    color: line.color,
  }));
}

function buildRecordFromDraft(
  record: CostPaymentRecordParam,
  draft: RecordDraft,
  locale: Locale,
): CostPaymentRecordParam {
  const lineItems = draft.lineItems
    .map((lineItem, index) => {
      const amount = parseMoneyInput(lineItem.amount);
      const allocations = lineItem.allocations
        .map((allocation, allocationIndex) => {
          const label =
            allocation.label.trim() || getAllocationLabel(allocation.kind);

          return {
            id: allocation.id || `${lineItem.id}-allocation-${allocationIndex}`,
            kind: allocation.kind,
            label,
            amount: parseMoneyInput(allocation.amount),
          };
        })
        .filter(
          (allocation) =>
            allocation.amount > 0 || allocation.label.trim().length > 0,
        );

      return {
        id: lineItem.id || `${record.id}-item-${index}`,
        label: lineItem.label.trim() || `Item ${index + 1}`,
        amount:
          amount ||
          allocations.reduce((sum, allocation) => sum + allocation.amount, 0),
        detail: lineItem.detail.trim(),
        allocations,
      };
    })
    .filter(
      (lineItem) =>
        lineItem.amount > 0 ||
        lineItem.label.trim().length > 0 ||
        lineItem.allocations.length > 0,
    );
  const billedAmount = lineItems.reduce(
    (sum, lineItem) => sum + lineItem.amount,
    0,
  );
  const splitLines = aggregateSplitLines(lineItems);

  return {
    ...record,
    label: draft.label.trim() || record.label,
    provider: draft.provider.trim() || record.provider,
    occurredOn: draft.date || record.occurredOn,
    date: draft.date ? formatDate(draft.date, locale) : record.date,
    amount: billedAmount,
    billedAmount,
    status: draft.status,
    detail: draft.detail.trim() || record.detail,
    note: draft.detail.trim() || null,
    splitLines,
    lineItems,
    headline: record.headline,
  };
}

function getOverviewSegments(
  record: CostPaymentRecordParam,
): CostPaymentSplitLineParam[] {
  const visibleLines = record.splitLines.filter((line) => line.amount > 0);
  const amount = getOverviewAmount(record);

  if (visibleLines.length > 0) return visibleLines;
  if (amount == null) return [];

  return [
    {
      id: `${record.id}-total`,
      label: getPrimaryAmountLabel(record),
      amount,
      detail: record.source,
      color: getFallbackSegmentColor(record),
    },
  ];
}

function PaymentSplitBar({ lines }: { lines: CostPaymentSplitLineParam[] }) {
  const visibleLines = lines.filter((line) => line.amount > 0);
  const total = visibleLines.reduce((sum, line) => sum + line.amount, 0);

  return (
    <View
      style={{
        height: 12,
        borderRadius: 999,
        overflow: "hidden",
        flexDirection: "row",
        backgroundColor: "rgba(42, 37, 34, 0.08)",
      }}
    >
      {total > 0
        ? visibleLines.map((line) => (
            <View
              key={line.id}
              style={{
                flex: Math.max(line.amount, total * 0.035),
                backgroundColor: getSegmentDisplayColor(line),
              }}
            />
          ))
        : null}
    </View>
  );
}

function PaymentSplitLegend({
  lines,
  locale,
}: {
  lines: CostPaymentSplitLineParam[];
  locale: Locale;
}) {
  const visibleLines = lines.filter((line) => line.amount > 0);

  if (visibleLines.length === 0) return null;

  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 8,
      }}
    >
      {visibleLines.map((line) => (
        <View key={line.id} style={{ flex: 1, gap: 3 }}>
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 6,
            }}
          >
            <View
              style={{
                width: 8,
                height: 8,
                borderRadius: 4,
                backgroundColor: getSegmentDisplayColor(line),
              }}
            />
            <Text
              className="font-dm-sans"
              numberOfLines={1}
              style={{
                flex: 1,
                fontSize: 12,
                color: EMBER.colors.textLight,
                fontWeight: "700",
              }}
            >
              {line.label}
            </Text>
          </View>
          <Text
            className="font-dm-mono"
            style={{
              fontSize: 13,
              fontWeight: "800",
              color: EMBER.colors.text,
            }}
          >
            {formatCurrency(line.amount, locale)}
          </Text>
        </View>
      ))}
    </View>
  );
}

function PaymentRecordOverview({
  record,
  locale,
}: {
  record: CostPaymentRecordParam;
  locale: Locale;
}) {
  const amount = getOverviewAmount(record);
  const segments = getOverviewSegments(record);
  const amountLabel = getPrimaryAmountLabel(record);
  const singleSegment = segments.length === 1 ? segments[0] : null;

  return (
    <View style={{ gap: 9 }}>
      <View
        style={{
          flexDirection: "row",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <View style={{ flex: 1, minWidth: 0, gap: 3 }}>
          <Text
            className="font-dm-mono"
            style={{
              fontSize: 27,
              lineHeight: 31,
              fontWeight: "800",
              color: colors.greyBrown,
            }}
          >
            {amount == null ? "Pending" : formatCurrency(amount, locale)}
          </Text>
          {amountLabel ? (
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12,
                fontWeight: "800",
                color: EMBER.colors.textLight,
              }}
            >
              {amountLabel}
            </Text>
          ) : null}
        </View>

        {singleSegment ? (
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 6,
              paddingTop: 6,
            }}
          >
            <View
              style={{
                width: 8,
                height: 8,
                borderRadius: 4,
                backgroundColor: getSegmentDisplayColor(singleSegment),
              }}
            />
            <Text
              className="font-dm-sans"
              numberOfLines={1}
              style={{
                fontSize: 12,
                color: EMBER.colors.textLight,
                fontWeight: "700",
              }}
            >
              {singleSegment.label}
            </Text>
          </View>
        ) : null}
      </View>

      {segments.length > 0 ? (
        <>
          <PaymentSplitBar lines={segments} />
          {segments.length > 1 ? (
            <PaymentSplitLegend lines={segments} locale={locale} />
          ) : null}
        </>
      ) : null}
    </View>
  );
}

/** A block of the record's details. It sits straight in the dialog. */
function DetailSection({ children }: { children: ReactNode }) {
  return <View style={{ gap: 9 }}>{children}</View>;
}

function DocumentEmptyState({ onAddDocument }: { onAddDocument?: () => void }) {
  const t = useTranslations();
  return (
    <EmptyState
      density="compact"
      illustration={
        <DialogIcon
          source={TAX_EXPENSE_RECEIPT_ILLUSTRATION}
          width={68}
          height={80}
        />
      }
      title={t("empty_states.no_document_uploaded")}
      subtitle={t("empty_states.add_a_receipt_or_claim_file")}
      action={
        onAddDocument ? (
          <CompactActionButton
            label={t("empty_states.add_document")}
            action="empty_states.add_document"
            variant="primary"
            leadingIcon={UploadCloud}
            onPress={onAddDocument}
          />
        ) : undefined
      }
      style={{ paddingHorizontal: 0 }}
    />
  );
}

function DocumentRows({
  documents,
  onViewDocument,
  onDeleteDocument,
}: {
  documents: CostPaymentDocumentParam[];
  onViewDocument?: (documentId: string) => void;
  onDeleteDocument?: (documentId: string) => void | Promise<void>;
}) {
  // Two-step delete: tap the trash once to arm, tap again to confirm. Keeps
  // the row compact without a modal and makes accidental taps recoverable.
  const [armedDeleteId, setArmedDeleteId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  return (
    <View style={{ gap: 0 }}>
      {documents.map((document, index) => {
        const canTap = !!onViewDocument;
        const canDelete = !!onDeleteDocument;
        const isArmed = armedDeleteId === document.id;
        const isDeleting = deletingId === document.id;
        const body = (
          <>
            <FileText
              size={15}
              color={EMBER.colors.textLight}
              strokeWidth={1.65}
              style={{ marginTop: 1 }}
            />
            <View style={{ flex: 1, minWidth: 0, gap: 4 }}>
              <Text
                className="font-dm-sans"
                numberOfLines={1}
                style={{
                  fontSize: 12,
                  fontWeight: "800",
                  color: EMBER.colors.text,
                }}
              >
                {document.label}
              </Text>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 11,
                  lineHeight: 16,
                  color: EMBER.colors.textLight,
                }}
              >
                {document.detail}
              </Text>
            </View>
            {canTap ? (
              <ChevronRight
                size={14}
                color={EMBER.colors.textFaint}
                strokeWidth={1.5}
                style={{ marginTop: 3 }}
              />
            ) : null}
          </>
        );
        const rowStyle = {
          borderTopWidth: index > 0 ? 1 : 0,
          borderTopColor: semanticColors.border.subtle,
          paddingTop: index > 0 ? 9 : 0,
          paddingBottom: index < documents.length - 1 ? 9 : 0,
          flexDirection: "row" as const,
          alignItems: "center" as const,
          gap: 9,
        };
        const deleteButton = canDelete ? (
          <IconButton
            accessibilityLabel={
              isArmed
                ? `Confirm delete ${document.label}`
                : `Delete ${document.label}`
            }
            action={isArmed ? "confirm_delete_document" : "arm_delete_document"}
            size={14}
            hitSlop={8}
            pressedOpacity={1}
            onPress={async () => {
              if (!onDeleteDocument) return;
              if (!isArmed) {
                setArmedDeleteId(document.id);
                return;
              }
              setDeletingId(document.id);
              try {
                await onDeleteDocument(document.id);
              } finally {
                setDeletingId(null);
                setArmedDeleteId(null);
              }
            }}
          >
            <Trash2
              size={14}
              color={isArmed ? EMBER.colors.coral : EMBER.colors.textLight}
              strokeWidth={1.7}
            />
          </IconButton>
        ) : null;
        const content = (
          <>
            {canTap ? (
              <Pressable
                onPress={() => onViewDocument?.(document.id)}
                disabled={isDeleting}
                accessibilityRole="button"
                accessibilityLabel={`Open ${document.label}`}
                style={{
                  flex: 1,
                  flexDirection: "row",
                  alignItems: "flex-start",
                  gap: 9,
                }}
              >
                {body}
              </Pressable>
            ) : (
              <View
                style={{
                  flex: 1,
                  flexDirection: "row",
                  alignItems: "flex-start",
                  gap: 9,
                }}
              >
                {body}
              </View>
            )}
            {deleteButton}
          </>
        );
        return (
          <View key={document.id} style={rowStyle}>
            {content}
          </View>
        );
      })}
    </View>
  );
}

function EditInput({
  label,
  value,
  onChangeText,
  placeholder,
  keyboardType = "default",
  multiline = false,
}: {
  label: string;
  value: string;
  onChangeText: (value: string) => void;
  placeholder: string;
  keyboardType?: "default" | "decimal-pad";
  multiline?: boolean;
}) {
  return (
    <TextField
      label={label}
      value={value}
      onChangeText={onChangeText}
      placeholder={placeholder}
      keyboardType={keyboardType}
      notes={multiline}
    />
  );
}

function EditMoneyInput({
  label,
  value,
  locale,
  onChangeText,
}: {
  label: string;
  value: string;
  locale: Locale;
  onChangeText: (value: string) => void;
}) {
  const currencyAffixes = getCurrencyAffixes(locale);

  return (
    <MoonNumericField
      label={label}
      value={value.trim() ? parseMoneyInput(value) : null}
      onChange={(next) => onChangeText(next == null ? "" : String(next))}
      decimals={2}
      min={0}
      prefix={currencyAffixes.prefix}
      suffix={currencyAffixes.suffix}
      placeholder="0.00"
    />
  );
}

function StatusSelector({
  value,
  onChange,
}: {
  value: CostPaymentRecordStatus;
  onChange: (status: CostPaymentRecordStatus) => void;
}) {
  return (
    <MoonFormField label="Status">
      <ChoiceChips
        options={PAYMENT_STATUS_OPTIONS.map((option) => ({
          id: option.value,
          label: option.label,
        }))}
        value={value}
        onChange={(next) => {
          const match = PAYMENT_STATUS_OPTIONS.find(
            (option) => option.value === next,
          );
          if (match) onChange(match.value);
        }}
      />
    </MoonFormField>
  );
}

function AllocationKindSelector({
  value,
  onChange,
}: {
  value: CostPaymentAllocationKind;
  onChange: (kind: CostPaymentAllocationKind) => void;
}) {
  return (
    <ChoiceChips
      options={ALLOCATION_KIND_OPTIONS.map((option) => ({
        id: option.value,
        label: option.label,
      }))}
      value={value}
      onChange={(next) => {
        const match = ALLOCATION_KIND_OPTIONS.find(
          (option) => option.value === next,
        );
        if (match) onChange(match.value);
      }}
    />
  );
}

function AllocationEditor({
  allocation,
  locale,
  canRemove,
  onChange,
  onRemove,
}: {
  allocation: AllocationDraft;
  locale: Locale;
  canRemove: boolean;
  onChange: (allocation: AllocationDraft) => void;
  onRemove: () => void;
}) {
  return (
    <View
      style={{
        borderTopWidth: 1,
        borderTopColor: semanticColors.border.subtle,
        paddingTop: 9,
        gap: 8,
      }}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
        }}
      >
        {/* The picker takes the room left beside the remove button. */}
        <View style={{ flex: 1, minWidth: 0 }}>
          <AllocationKindSelector
            value={allocation.kind}
            onChange={(kind) =>
              onChange({
                ...allocation,
                kind,
                label: getAllocationLabel(kind),
              })
            }
          />
        </View>
        {canRemove ? (
          <IconButton
            accessibilityLabel="Remove payer"
            action="remove_allocation"
            onPress={onRemove}
            size={14}
            hitSlop={8}
            pressedOpacity={1}
          >
            <Trash2
              size={14}
              color={EMBER.colors.textLight}
              strokeWidth={1.7}
            />
          </IconButton>
        ) : null}
      </View>

      <View style={{ flexDirection: "row", gap: 9 }}>
        <View style={{ flex: 1 }}>
          <EditInput
            label="Payer label"
            value={allocation.label}
            onChangeText={(label) => onChange({ ...allocation, label })}
            placeholder="Aetna, you, employer benefit..."
          />
        </View>
        <View style={{ width: 112 }}>
          <EditMoneyInput
            label="Amount"
            value={allocation.amount}
            locale={locale}
            onChangeText={(amount) => onChange({ ...allocation, amount })}
          />
        </View>
      </View>
    </View>
  );
}

function LineItemEditor({
  lineItem,
  locale,
  canRemove,
  onChange,
  onRemove,
}: {
  lineItem: LineItemDraft;
  locale: Locale;
  canRemove: boolean;
  onChange: (lineItem: LineItemDraft) => void;
  onRemove: () => void;
}) {
  const itemAmount = parseMoneyInput(lineItem.amount);
  const allocatedAmount = lineItem.allocations.reduce(
    (sum, allocation) => sum + parseMoneyInput(allocation.amount),
    0,
  );
  const difference = itemAmount - allocatedAmount;
  const balanced = Math.abs(difference) < 1;
  const statusCopy = balanced
    ? "Fully allocated"
    : difference > 0
      ? `${formatCurrency(difference, locale)} unassigned`
      : `${formatCurrency(Math.abs(difference), locale)} over`;
  const statusColor = balanced ? EMBER.colors.textLight : EMBER.colors.coral;
  const updateAllocation = (
    allocationId: string,
    allocation: AllocationDraft,
  ) => {
    onChange({
      ...lineItem,
      allocations: lineItem.allocations.map((currentAllocation) =>
        currentAllocation.id === allocationId ? allocation : currentAllocation,
      ),
    });
  };
  const removeAllocation = (allocationId: string) => {
    onChange({
      ...lineItem,
      allocations: lineItem.allocations.filter(
        (allocation) => allocation.id !== allocationId,
      ),
    });
  };
  const addAllocation = () => {
    onChange({
      ...lineItem,
      allocations: [
        ...lineItem.allocations,
        {
          id: `${lineItem.id}-allocation-${Date.now()}`,
          kind: "patient",
          label: "Paid by you",
          amount: "",
        },
      ],
    });
  };

  return (
    <CardSurface variant="panel" style={{ padding: 10, gap: 10 }}>
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
        }}
      >
        <View style={{ flex: 1, minWidth: 0, gap: 2 }}>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              fontWeight: "800",
              color: EMBER.colors.text,
            }}
          >
            Charge item
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 11,
              fontWeight: "700",
              color: statusColor,
            }}
          >
            {statusCopy}
          </Text>
        </View>
        {canRemove ? (
          <IconButton
            accessibilityLabel="Remove charge item"
            action="remove_charge_item"
            onPress={onRemove}
            size={14}
            hitSlop={8}
            pressedOpacity={1}
          >
            <Trash2
              size={14}
              color={EMBER.colors.textLight}
              strokeWidth={1.7}
            />
          </IconButton>
        ) : null}
      </View>

      <ChoiceChips
        options={LINE_ITEM_PRESETS.map((preset) => ({
          id: preset.label,
          label: preset.label,
          icon: preset.icon,
          action: "charge_item_preset",
        }))}
        value={lineItem.label}
        onChange={(preset) => onChange({ ...lineItem, label: preset })}
      />

      <View style={{ flexDirection: "row", gap: 9 }}>
        <View style={{ flex: 1 }}>
          <EditInput
            label="Item"
            value={lineItem.label}
            onChangeText={(label) => onChange({ ...lineItem, label })}
            placeholder="Carrier screening"
          />
        </View>
        <View style={{ width: 112 }}>
          <EditMoneyInput
            label="Cost"
            value={lineItem.amount}
            locale={locale}
            onChangeText={(amount) => onChange({ ...lineItem, amount })}
          />
        </View>
      </View>

      <EditInput
        label="Detail"
        value={lineItem.detail}
        onChangeText={(detail) => onChange({ ...lineItem, detail })}
        placeholder="Quote line, claim line, statement detail..."
      />

      <View style={{ gap: 9 }}>
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
          }}
        >
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 11,
              fontWeight: "800",
              color: EMBER.colors.textLight,
            }}
          >
            Who pays this
          </Text>
          <CompactActionButton
            label="Add payer"
            size="sm"
            variant="secondary"
            leadingIcon={Plus}
            onPress={addAllocation}
          />
        </View>

        {lineItem.allocations.map((allocation) => (
          <AllocationEditor
            key={allocation.id}
            allocation={allocation}
            locale={locale}
            canRemove={lineItem.allocations.length > 1}
            onChange={(nextAllocation) =>
              updateAllocation(allocation.id, nextAllocation)
            }
            onRemove={() => removeAllocation(allocation.id)}
          />
        ))}
      </View>
    </CardSurface>
  );
}

function RecordEditForm({
  draft,
  locale,
  onChangeDraft,
  onCancel,
  onSave,
  isSaving,
  onAttachDocument,
  hasAttachedDocument,
}: {
  draft: RecordDraft;
  locale: Locale;
  onChangeDraft: (draft: RecordDraft) => void;
  onCancel: () => void;
  onSave: () => void;
  isSaving: boolean;
  onAttachDocument?: () => void;
  hasAttachedDocument: boolean;
}) {
  const capturePress = usePressCapture("RecordEditForm");
  const t = useTranslations();
  const updateLineItem = (lineItemId: string, lineItem: LineItemDraft) => {
    onChangeDraft({
      ...draft,
      lineItems: draft.lineItems.map((currentLineItem) =>
        currentLineItem.id === lineItemId ? lineItem : currentLineItem,
      ),
    });
  };
  const removeLineItem = (lineItemId: string) => {
    onChangeDraft({
      ...draft,
      lineItems: draft.lineItems.filter(
        (lineItem) => lineItem.id !== lineItemId,
      ),
    });
  };
  const addLineItem = () => {
    const lineItemId = `edited-item-${Date.now()}`;

    onChangeDraft({
      ...draft,
      lineItems: [
        ...draft.lineItems,
        {
          id: lineItemId,
          label: "",
          amount: "",
          detail: "",
          allocations: [
            {
              id: `${lineItemId}-patient`,
              kind: "patient",
              label: "Paid by you",
              amount: "",
            },
          ],
        },
      ],
    });
  };

  return (
    <View style={{ gap: 14 }}>
      <DetailSection>
        <View style={{ gap: 12 }}>
          <View style={{ flexDirection: "row", gap: 9 }}>
            <View style={{ flex: 1 }}>
              <DateField
                label="Date"
                value={draft.date}
                onChange={(date) => onChangeDraft({ ...draft, date })}
              />
            </View>
          </View>

          <EditInput
            label="Row title"
            value={draft.label}
            onChangeText={(label) => onChangeDraft({ ...draft, label })}
            placeholder="Clinic deposit, medication receipt..."
          />
          <EditInput
            label="Provider"
            value={draft.provider}
            onChangeText={(provider) => onChangeDraft({ ...draft, provider })}
            placeholder="Clinic, pharmacy, insurer..."
          />
          <StatusSelector
            value={draft.status}
            onChange={(status) => onChangeDraft({ ...draft, status })}
          />
          <EditInput
            label="Notes"
            value={draft.detail}
            onChangeText={(detail) => onChangeDraft({ ...draft, detail })}
            placeholder="Anything that helps reconcile this later"
            multiline
          />
          {onAttachDocument ? (
            <CompactActionButton
              label={
                hasAttachedDocument
                  ? t("costs.cost_upload.add_another_document")
                  : t("costs.cost_upload.attach_receipt_eob_statement_or_claim")
              }
              onPress={() => {
                capturePress(
                  pressLabel(
                    hasAttachedDocument
                      ? "Add another document"
                      : "Attach document",
                  ),
                  "attach_document_from_edit",
                );
                onAttachDocument();
              }}
              size="sm"
              variant="secondary"
              leading={
                hasAttachedDocument ? (
                  <Plus size={13} color={colors.greyBrown} strokeWidth={1.9} />
                ) : (
                  <UploadCloud
                    size={13}
                    color={colors.greyBrown}
                    strokeWidth={1.8}
                  />
                )
              }
            />
          ) : null}
        </View>
      </DetailSection>

      <DetailSection>
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <SectionTitle level="subsection">Charge items</SectionTitle>
          <CompactActionButton
            label="Add item"
            size="sm"
            variant="secondary"
            leadingIcon={Plus}
            onPress={addLineItem}
          />
        </View>

        {draft.lineItems.map((lineItem) => (
          <LineItemEditor
            key={lineItem.id}
            lineItem={lineItem}
            locale={locale}
            canRemove={draft.lineItems.length > 1}
            onChange={(nextLineItem) =>
              updateLineItem(lineItem.id, nextLineItem)
            }
            onRemove={() => removeLineItem(lineItem.id)}
          />
        ))}
      </DetailSection>

      <View style={{ flexDirection: "row", gap: 9 }}>
        <SecondaryButton label="Cancel" onPress={onCancel} flex={1} />
        <PrimaryButton
          label="Save changes"
          onPress={onSave}
          loading={isSaving}
          flex={1}
        />
      </View>
    </View>
  );
}

function NotesInput({
  value,
  onChangeText,
}: {
  value: string;
  onChangeText: (text: string) => void;
}) {
  return (
    <TextField
      label="Notes"
      value={value}
      onChangeText={onChangeText}
      placeholder="Add a note about this payment, claim, receipt, or EOB"
      notes
    />
  );
}

function ActionButton({ action }: { action: PaymentAction }) {
  return (
    <CompactActionButton
      label={action.label}
      onPress={action.onPress}
      size="sm"
      variant={action.selected ? "selected" : "secondary"}
      leadingIcon={action.icon}
      iconStrokeWidth={1.85}
    />
  );
}

function HeaderInfoChip({
  label,
  icon: Icon,
  initial,
}: {
  label: string;
  icon?: LucideIcon;
  initial?: string;
}) {
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: 5,
        paddingHorizontal: 10,
        paddingVertical: 5,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: semanticColors.border.subtle,
      }}
    >
      {initial ? (
        <View
          style={{
            width: 13,
            height: 13,
            borderRadius: 7,
            borderWidth: 1,
            borderColor: semanticColors.border.strong,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 8,
              lineHeight: 10,
              fontWeight: "800",
              color: EMBER.colors.textLight,
            }}
          >
            {initial}
          </Text>
        </View>
      ) : Icon ? (
        <Icon size={10} color={EMBER.colors.textFaint} strokeWidth={1.5} />
      ) : null}
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 11,
          fontWeight: "500",
          color: EMBER.colors.textLight,
          letterSpacing: 0.2,
        }}
      >
        {label}
      </Text>
    </View>
  );
}

function HeaderChipRow({
  record,
  statusBadge,
}: {
  record: CostPaymentRecordParam;
  statusBadge: { label: string; tone: StatusBadgeTone };
}) {
  const providerInitial = record.provider.trim().charAt(0).toUpperCase();
  const StatusIcon = getStatusIcon(record.status);

  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        flexWrap: "wrap",
        gap: 7,
      }}
    >
      {record.provider ? (
        <HeaderInfoChip
          label={record.provider}
          icon={providerInitial ? undefined : Building2}
          initial={providerInitial || undefined}
        />
      ) : null}
      <HeaderInfoChip label={statusBadge.label} icon={StatusIcon} />
    </View>
  );
}

function RecordIllustration() {
  return (
    <Image
      source={TAX_EXPENSE_RECEIPT_ILLUSTRATION}
      accessibilityIgnoresInvertColors
      style={{ width: 76, height: 76, alignSelf: "center" }}
      resizeMode="contain"
    />
  );
}

export default function PaymentRecordDetailModal({
  record,
  locale,
  onClose,
  onAttachDocument,
  onDeleteDocument,
  onReviewInsurance,
  onOpenFinancing,
  onOpenTaxTracker,
  onAskChat,
  onSaveRecord,
  onDeleteRecord,
  taxReviewDecision = "unknown",
  onTaxReviewDecision,
  onResetTaxReviewDecision,
  taxEligibility,
  onTaxEligibilityChange,
}: Props) {
  const t = useTranslations();
  const statusBadge = getStatusBadge(record.status);
  const [noteText, setNoteText] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isSavingTaxDecision, setIsSavingTaxDecision] = useState(false);
  const [taxEligibilityDraft, setTaxEligibilityDraft] = useState(
    taxEligibility ?? UNKNOWN_TAX_ELIGIBILITY,
  );
  const [draft, setDraft] = useState(() => createDraftFromRecord(record));
  // Preview the attached document in a stacked modal rather than navigating
  // the patient away from Costs. useDocument is enabled only when there's a
  // selected id, so the fetch doesn't fire until the user taps a row.
  const [previewDocumentId, setPreviewDocumentId] = useState<string | null>(
    null,
  );
  const previewDocumentQuery = useDocument(previewDocumentId ?? "");
  const uploadedDocuments = record.documents.filter(
    (document) => document.status === "attached",
  );
  const previewDocumentInfo: PreviewDocumentInfo = (() => {
    if (!previewDocumentId || !previewDocumentQuery.data) return null;
    const matching = uploadedDocuments.find(
      (candidate) => candidate.id === previewDocumentId,
    );
    const label = matching?.label ?? previewDocumentQuery.data.name;
    return {
      slotId: previewDocumentId,
      slotName: label,
      slotWhy: "",
      document: previewDocumentQuery.data,
    };
  })();
  const actions = buildActions({
    onAttachDocument,
    hasUploadedDocuments: uploadedDocuments.length > 0,
    onReviewInsurance,
    onOpenFinancing,
    onOpenTaxTracker,
    onAskChat,
    record,
  });

  useEffect(() => {
    setDraft(createDraftFromRecord(record));
    setNoteText(record.note ?? "");
    setIsEditing(false);
  }, [record]);

  const handleCancelEdit = () => {
    setDraft(createDraftFromRecord(record));
    setIsEditing(false);
  };
  const handleSaveEdit = async () => {
    const nextRecord = buildRecordFromDraft(record, draft, locale);
    if (!onSaveRecord) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      await onSaveRecord(nextRecord);
      setNoteText(nextRecord.note ?? "");
      setIsEditing(false);
    } catch (error) {
      console.error("Failed to save payment record:", error);
      setSaveError(
        "We couldn't save these changes. Check your connection and try again.",
      );
    } finally {
      setIsSaving(false);
    }
  };
  const handleSaveNote = async () => {
    if (!onSaveRecord) return;
    const nextNote = noteText.trim();
    setIsSaving(true);
    setSaveError(null);
    try {
      await onSaveRecord({
        ...record,
        note: nextNote || null,
        detail: nextNote || record.detail,
      });
    } catch (error) {
      console.error("Failed to save payment note:", error);
      setSaveError(
        "We couldn't save that note. Check your connection and try again.",
      );
    } finally {
      setIsSaving(false);
    }
  };
  const noteChanged = noteText.trim() !== (record.note ?? "").trim();
  useEffect(() => {
    setTaxEligibilityDraft(taxEligibility ?? UNKNOWN_TAX_ELIGIBILITY);
  }, [taxEligibility]);

  const saveTaxEligibility = async (next: CostPaymentTaxEligibility) => {
    if (!onTaxEligibilityChange) return;
    const previous = taxEligibilityDraft;
    setTaxEligibilityDraft(next);
    setSaveError(null);
    try {
      await onTaxEligibilityChange(next);
    } catch (error) {
      setTaxEligibilityDraft(previous);
      setSaveError(
        error instanceof Error
          ? error.message
          : "We couldn't save the tax eligibility details. Try again.",
      );
    }
  };
  const saveTaxDecision = async (
    decision: Exclude<TaxReviewDecision, "unknown">,
  ) => {
    if (!onTaxReviewDecision) return;
    setIsSavingTaxDecision(true);
    setSaveError(null);
    try {
      await onTaxReviewDecision(decision);
    } catch (error) {
      setSaveError(
        error instanceof Error
          ? error.message
          : "We couldn't save that tax review decision. Try again.",
      );
    } finally {
      setIsSavingTaxDecision(false);
    }
  };
  const resetTaxDecision = async () => {
    if (!onResetTaxReviewDecision) return;
    setIsSavingTaxDecision(true);
    setSaveError(null);
    try {
      await onResetTaxReviewDecision();
    } catch (error) {
      setSaveError(
        error instanceof Error
          ? error.message
          : "We couldn't reset that tax review decision. Try again.",
      );
    } finally {
      setIsSavingTaxDecision(false);
    }
  };

  return (
    <>
      <Dialog
        size="wide"
        visible
        onClose={onClose}
        layout="centered"
        closeButton="minimal"
        closeAccessibilityLabel="Close payment record"
        surfaceStyle={{
          maxHeight: "100%",
        }}
        contentStyle={{
          maxHeight: "100%",
          paddingTop: 0,
          paddingHorizontal: 0,
          paddingBottom: 0,
        }}
      >
        <ScrollView
          style={{ maxHeight: "100%" }}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{ padding: 18, gap: 16 }}
        >
          <View style={{ gap: 12, alignItems: "center" }}>
            <RecordIllustration />
            <View style={{ gap: 6, alignItems: "center" }}>
              <DialogHeader align="center" title={record.label} />
              <HeaderChipRow record={record} statusBadge={statusBadge} />
            </View>
          </View>

          {onSaveRecord && !isEditing ? (
            <IconButton
              onPress={() => setIsEditing(true)}
              hitSlop={8}
              accessibilityLabel="Edit payment record"
              size={30}
              // Mirrors the dialog's close in the opposite corner.
              style={{
                position: "absolute",
                top: 11,
                left: 11,
                zIndex: 2,
              }}
            >
              <Pencil
                size={17}
                color={EMBER.colors.textLight}
                strokeWidth={1.7}
              />
            </IconButton>
          ) : null}

          <PaymentRecordOverview record={record} locale={locale} />

          {saveError ? (
            <ActionBanner variant="important" message={saveError} />
          ) : null}

          {isEditing ? (
            <RecordEditForm
              draft={draft}
              locale={locale}
              onChangeDraft={setDraft}
              onCancel={handleCancelEdit}
              onSave={handleSaveEdit}
              isSaving={isSaving}
              onAttachDocument={onAttachDocument}
              hasAttachedDocument={uploadedDocuments.length > 0}
            />
          ) : (
            <>
              <DetailSection>
                <NotesInput value={noteText} onChangeText={setNoteText} />
                {onSaveRecord && noteChanged ? (
                  <CompactActionButton
                    label="Save note"
                    variant="primary"
                    size="sm"
                    loading={isSaving}
                    onPress={() => void handleSaveNote()}
                    style={{ alignSelf: "flex-start" }}
                  />
                ) : null}
              </DetailSection>

              <DetailSection>
                {uploadedDocuments.length > 0 ? (
                  <DocumentRows
                    documents={uploadedDocuments}
                    onViewDocument={(documentId) =>
                      setPreviewDocumentId(documentId)
                    }
                    onDeleteDocument={onDeleteDocument}
                  />
                ) : (
                  <DocumentEmptyState onAddDocument={onAttachDocument} />
                )}
              </DetailSection>

              {onTaxEligibilityChange ? (
                <DetailSection>
                  <View>
                    <SectionTitle level="subsection">
                      {t("costs.tax_review.tax_eligibility")}
                    </SectionTitle>
                    <CardSubtitleText>
                      {t("costs.tax_review.tax_eligibility_body")}
                    </CardSubtitleText>
                  </View>
                  <MoonFormField label={t("costs.tax_review.tax_expense_type")}>
                    <ChoiceChips
                      value={taxEligibilityDraft.expenseType}
                      onChange={(value) => {
                        if (
                          value !== "medical" &&
                          value !== "not_medical" &&
                          value !== "unknown"
                        ) {
                          return;
                        }
                        void saveTaxEligibility({
                          ...taxEligibilityDraft,
                          expenseType: value,
                        });
                      }}
                      options={[
                        {
                          id: "medical",
                          label: t("costs.tax_review.tax_expense_medical"),
                        },
                        {
                          id: "not_medical",
                          label: t("costs.tax_review.tax_expense_not_medical"),
                        },
                        {
                          id: "unknown",
                          label: t("costs.tax_review.tax_expense_unknown"),
                        },
                      ]}
                    />
                  </MoonFormField>
                  <MoonFormField
                    label={t("costs.tax_review.tax_care_recipient")}
                  >
                    <ChoiceChips
                      value={taxEligibilityDraft.careRecipient}
                      onChange={(value) => {
                        if (
                          value !== "self" &&
                          value !== "spouse" &&
                          value !== "dependent" &&
                          value !== "other" &&
                          value !== "unknown"
                        ) {
                          return;
                        }
                        void saveTaxEligibility({
                          ...taxEligibilityDraft,
                          careRecipient: value,
                        });
                      }}
                      options={[
                        {
                          id: "self",
                          label: t("costs.tax_review.tax_care_self"),
                        },
                        {
                          id: "spouse",
                          label: t("costs.tax_review.tax_care_spouse"),
                        },
                        {
                          id: "dependent",
                          label: t("costs.tax_review.tax_care_dependent"),
                        },
                        {
                          id: "other",
                          label: t("costs.tax_review.tax_care_other"),
                        },
                        {
                          id: "unknown",
                          label: t("costs.tax_review.tax_care_unknown"),
                        },
                      ]}
                    />
                  </MoonFormField>
                  <MoonFormField
                    label={t("costs.tax_review.tax_insurance_outcome")}
                  >
                    <ChoiceChips
                      value={taxEligibilityDraft.insuranceOutcome}
                      onChange={(value) => {
                        if (
                          value !== "not_applicable" &&
                          value !== "not_reimbursed" &&
                          value !== "partially_reimbursed" &&
                          value !== "fully_reimbursed" &&
                          value !== "unknown"
                        ) {
                          return;
                        }
                        void saveTaxEligibility({
                          ...taxEligibilityDraft,
                          insuranceOutcome: value,
                        });
                      }}
                      options={[
                        {
                          id: "not_applicable",
                          label: t(
                            "costs.tax_review.tax_insurance_not_applicable",
                          ),
                        },
                        {
                          id: "not_reimbursed",
                          label: t(
                            "costs.tax_review.tax_insurance_not_reimbursed",
                          ),
                        },
                        {
                          id: "partially_reimbursed",
                          label: t(
                            "costs.tax_review.tax_insurance_partially_reimbursed",
                          ),
                        },
                        {
                          id: "fully_reimbursed",
                          label: t(
                            "costs.tax_review.tax_insurance_fully_reimbursed",
                          ),
                        },
                        {
                          id: "unknown",
                          label: t("costs.tax_review.tax_insurance_unknown"),
                        },
                      ]}
                    />
                  </MoonFormField>
                </DetailSection>
              ) : null}

              {onTaxReviewDecision ? (
                <DetailSection>
                  <View>
                    <SectionTitle level="subsection">
                      {t("costs.tax_review.tax_review_decision")}
                    </SectionTitle>
                    <CardSubtitleText>
                      {t("costs.tax_review.tax_review_decision_body")}
                    </CardSubtitleText>
                  </View>
                  <View
                    style={{
                      flexDirection: "row",
                      flexWrap: "wrap",
                      gap: 9,
                    }}
                  >
                    <View style={{ flex: 1, minWidth: 160 }}>
                      <ActionButton
                        action={{
                          label: t(
                            "costs.tax_review.include_expense_in_review",
                          ),
                          icon: CheckCircle2,
                          onPress: () => void saveTaxDecision("include"),
                          selected: taxReviewDecision === "include",
                        }}
                      />
                    </View>
                    <View style={{ flex: 1, minWidth: 160 }}>
                      <ActionButton
                        action={{
                          label: t(
                            "costs.tax_review.exclude_expense_from_review",
                          ),
                          icon: X,
                          onPress: () => void saveTaxDecision("exclude"),
                          selected: taxReviewDecision === "exclude",
                        }}
                      />
                    </View>
                  </View>
                  {taxReviewDecision !== "unknown" &&
                  onResetTaxReviewDecision ? (
                    <TextActionButton
                      label={t(
                        "costs.tax_review.reset_expense_review_decision",
                      )}
                      action="costs.tax_review.reset_expense_review_decision"
                      tone="tertiary"
                      size="sm"
                      onPress={() => void resetTaxDecision()}
                      disabled={isSavingTaxDecision}
                      style={{ alignSelf: "center" }}
                    />
                  ) : null}
                </DetailSection>
              ) : null}

              {actions.length > 0 ? (
                <View
                  style={{
                    flexDirection: "row",
                    flexWrap: "wrap",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: 9,
                  }}
                >
                  {actions.map((action) => (
                    <View key={action.label}>
                      <ActionButton action={action} />
                    </View>
                  ))}
                </View>
              ) : null}

              {onDeleteRecord ? (
                <TextActionButton
                  label="Delete record"
                  accessibilityLabel="Delete payment record"
                  tone="destructive"
                  size="sm"
                  onPress={() => {
                    setDeleteError(null);
                    setDeleteConfirmVisible(true);
                  }}
                  style={{ alignSelf: "center" }}
                />
              ) : null}
            </>
          )}
        </ScrollView>
      </Dialog>

      <Dialog
        visible={deleteConfirmVisible}
        onClose={() => setDeleteConfirmVisible(false)}
        closeButton="minimal"
        contentStyle={{
          paddingTop: 28,
          paddingHorizontal: 20,
          paddingBottom: 20,
          gap: 14,
        }}
      >
        <DialogIcon source={DELETE_CONFIRMATION_ILLUSTRATION} />
        <DialogHeader
          align="center"
          title={t("costs.money_card.payments.delete_this_record")}
          subtitle={t("costs.money_card.payments.record_will_be_removed")}
        />
        {deleteError ? (
          <ActionBanner variant="important" message={deleteError} />
        ) : null}
        <DialogActions style={{ marginTop: 0 }}>
          <SecondaryButton
            label={t("common.actions.cancel")}
            onPress={() => setDeleteConfirmVisible(false)}
            disabled={isDeleting}
            flex={1}
          />
          <SecondaryButton
            variant="destructive"
            label={t("common.actions.delete")}
            onPress={async () => {
              if (!onDeleteRecord) return;
              setIsDeleting(true);
              setDeleteError(null);
              try {
                await onDeleteRecord();
                setDeleteConfirmVisible(false);
              } catch (error) {
                console.error("Failed to delete payment record:", error);
                setDeleteError(
                  t("costs.money_card.payments.couldnt_delete_record"),
                );
              } finally {
                setIsDeleting(false);
              }
            }}
            loading={isDeleting}
            flex={1}
          />
        </DialogActions>
      </Dialog>
      <DocumentPreviewModal
        visible={!!previewDocumentId && !!previewDocumentInfo}
        onClose={() => setPreviewDocumentId(null)}
        documentInfo={previewDocumentInfo}
        onReupload={() => {
          // Reupload from the payment detail flow isn't meaningful — the
          // record already has an attachment pointer, and reuploading would
          // be a round-trip through the detach + reattach modal. Close the
          // preview and let the user use the attach flow from the detail
          // view if they want to swap the file.
          setPreviewDocumentId(null);
          onAttachDocument?.();
        }}
      />
    </>
  );
}
